import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {Box} from '@primer/styled-react'
import {memo, Suspense} from 'react'
import {graphql, useFragment} from 'react-relay'

import {Header} from '../components/list/header/Header'
import {HeaderLoading} from '../components/list/header/HeaderLoading'
import {Search} from '../components/search/Search'
import {useRouteInfo} from '../hooks/use-route-info'
import {useSearchMetrics} from '../hooks/use-search-metrics'
import type {ListCurrentViewFragment$key} from './__generated__/ListCurrentViewFragment.graphql'
import type {ListQuery$key} from './__generated__/ListQuery.graphql'
import type {ListRepositoryFragment$key} from './__generated__/ListRepositoryFragment.graphql'
import type {LoadSearchQuery} from './shared'

const LIST_CONTAINER_SX = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  maxWidth: '100%',
} as const

export type ListProps = {
  fetchedView: ListCurrentViewFragment$key
  queryFromCustomView?: string | null
  onSidePanelNavigate?: (issue: ItemIdentifier) => void
  showSsoBanner?: boolean
  fetchedRepository: ListRepositoryFragment$key | null
  search?: ListQuery$key
  loadSearchQuery?: LoadSearchQuery
  onCollapse?: () => void
  onListItemsChange?: (items: ItemIdentifier[]) => void
}

const ListInternal = ({
  fetchedView,
  fetchedRepository,
  search,
  loadSearchQuery,
  queryFromCustomView,
  onCollapse,
  onSidePanelNavigate,
  showSsoBanner,
  onListItemsChange,
}: ListProps) => {
  const currentViewData = useFragment<ListCurrentViewFragment$key>(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment ListCurrentViewFragment on Shortcutable {
        ...SearchCurrentViewFragment
        ...HeaderCurrentViewFragment
      }
    `,
    fetchedView,
  )

  const repoData = useFragment<ListRepositoryFragment$key>(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment ListRepositoryFragment on Repository {
        ...SearchRepositoryFragment
      }
    `,
    fetchedRepository || null,
  )

  const searchData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment ListQuery on Searchable
      @argumentDefinitions(
        query: {type: "String!"}
        first: {type: "Int"}
        labelPageSize: {type: "Int!"}
        skip: {type: "Int", defaultValue: null}
        fetchRepository: {type: "Boolean!"}
      ) {
        ...SearchRootFragment
          @arguments(
            query: $query
            first: $first
            labelPageSize: $labelPageSize
            skip: $skip
            fetchRepository: $fetchRepository
          )
      }
    `,
    search,
  )

  const {itemIdentifier} = useRouteInfo()

  const {trackIssueNavigation, updateIssueSearchType} = useSearchMetrics()

  return (
    <Box sx={LIST_CONTAINER_SX}>
      {currentViewData && (
        <Suspense fallback={<HeaderLoading />}>
          <Header currentViewKey={currentViewData} onCollapse={onCollapse} />
        </Suspense>
      )}
      {currentViewData && searchData && (
        <Search
          itemIdentifier={itemIdentifier}
          currentViewKey={currentViewData}
          currentRepository={repoData ?? null}
          search={searchData}
          loadSearchQuery={loadSearchQuery}
          queryFromCustomView={queryFromCustomView}
          onSidePanelNavigate={onSidePanelNavigate}
          showSsoBanner={showSsoBanner}
          trackIssueNavigation={trackIssueNavigation}
          updateIssueSearchType={updateIssueSearchType}
          onListItemsChange={onListItemsChange}
        />
      )}
    </Box>
  )
}

export const List = memo(ListInternal)
